import React from "react";
import ReactDOM from "react-dom/client";
import { intersection, keys, assign, omit } from "lodash-es";

const CLASS_ATTRIBUTE_NAME = "data-react-class";
const PROPS_ATTRIBUTE_NAME = "data-react-props";

const ReactComponent = {
  registeredComponents: {},
  componentRoots: new Map(), // Use Map to track multiple roots keyed by node

  render(node, component) {
    const propsJson = node.getAttribute(PROPS_ATTRIBUTE_NAME);
    const props = propsJson && JSON.parse(propsJson);

    const reactElement = React.createElement(component, props);
    const root = ReactDOM.createRoot(node);

    root.render(reactElement);

    return root;
  },

  registerComponents(components) {
    const collisions = intersection(
      keys(this.registeredComponents),
      keys(components)
    );
    if (collisions.length > 0) {
      console.error(
        `Following components are already registered: ${collisions}`
      );
    }

    assign(this.registeredComponents, omit(components, collisions));
    return true;
  },

  mountComponents() {
    const { registeredComponents, componentRoots } = this;
    const toMount = document.querySelectorAll(`[${CLASS_ATTRIBUTE_NAME}]`);

    // Clean up roots for nodes that no longer exist in the document
    for (const [node, root] of componentRoots.entries()) {
      if (!document.body.contains(node)) {
        root.unmount();
        componentRoots.delete(node);
      }
    }

    toMount.forEach(node => {
      const className = node.getAttribute(CLASS_ATTRIBUTE_NAME);
      const component = registeredComponents[className];

      if (component) {
        if (!componentRoots.has(node)) {
          // Only create a root and render if not already rendered on this node
          if (componentRoots.has(node)) {
            componentRoots.get(node).unmount();
            componentRoots.delete(node);
          }

          const root = this.render(node, component);
          componentRoots.set(node, root);
        }
      } else {
        console.error(
          `Can not render a component that has not been registered: ${className}`
        );
      }
    });
  },

  setup(components = {}) {
    if (typeof window.ReactComponent === "undefined") {
      window.ReactComponent = this;
    }

    window.ReactComponent.registerComponents(components);
    window.ReactComponent.mountComponents();

    document.addEventListener('turbo:load', () => {
      if (window.ReactComponent) {
        window.ReactComponent.mountComponents();
      }
    });

    document.addEventListener('turbo:render', () => {
      if (window.ReactComponent) {
        window.ReactComponent.mountComponents();
      }
    });
  },
};

export default ReactComponent;

